// libs
import React, { useCallback } from 'react'
import { match, RouteComponentProps, withRouter } from 'react-router-dom'

// interfaces
import { ILocationShape, IPosition } from 'src/interfaces/location'

// constants
import {
  EVENTS_FEED_PARTIAL,
  FRONTEND_ARTICLE_PATH,
  FRONTEND_EVENT_PATH,
  FRONTEND_PROFILES_DIRECTORY,
  FRONTEND_SNAPSHOT_PATH,
  getProjectRoutes,
  INFRASTRUCTURE_PROJECTS_PARTIAL,
  NEWS_FEED_PARTIAL,
  PUBLIC_PARTICIPATION_PARTIAL
} from 'src/constants/urls'

// helpers
import { NotificationsQuery, useGeoLookup } from '@lopo-tech/lopo-api'
import { useGetAppLocationShape } from 'src/reducers/app-state/app-state_hooks'
import { reportError } from 'src/utils/reporting/report-errors'
import {
  replaceLocationSlugFromPath,
  UrlUtils
} from 'src/utils/url/url'

type ResourceLink =
  NotificationsQuery['notifications']['items'][number]['resourceLink'];

export interface IURLContext {
  getEventsfeedRoute: (filter?: string) => string;
  getPublicParticipationRoute: (filter?: string) => string;
  getInfrastructureProjectsRoute: (filter?: string) => string;
  getNewsfeedRoute: (filter?: string) => string;
  getProfilesDirectoryRoute: () => string;
  getResourceLinkRoute: (resourceLink: ResourceLink, filterName?: string) => string;
  navigateToShape: (match: match, locationShape: ILocationShape) => void;
  navigateToPositionURL: (match: match, position: IPosition) => Promise<void>;
}

export const URLContext = React.createContext<IURLContext>({
  getEventsfeedRoute: () => '/',
  getPublicParticipationRoute: () => '/',
  getInfrastructureProjectsRoute: () => '/',
  getNewsfeedRoute: () => '/',
  getProfilesDirectoryRoute: () => '/',
  getResourceLinkRoute: () => '/',
  navigateToPositionURL: () => Promise.resolve(),
  navigateToShape: () => {
    /* will be populated with corect behavior */
  }
})

export const URLConsumer = URLContext.Consumer

const URLProvider: React.FC<RouteComponentProps> = ({ children, history }) => {
  const [lookUpLocation] = useGeoLookup()
  const locationShape = useGetAppLocationShape()
  const locationSlug = locationShape?.slug

  const getNewsfeedRoute = useCallback(
    (filter?: string) => {
      if (locationSlug) {
        return `/${locationSlug}${NEWS_FEED_PARTIAL}${
          filter ? `/${filter.toLocaleLowerCase()}` : ''
        }`
      }
      return '/'
    },
    [locationSlug]
  )
  const getEventsfeedRoute = useCallback(
    (filter?: string) => {
      if (locationSlug) {
        return `/${locationSlug}${EVENTS_FEED_PARTIAL}${
          filter ? `/${filter.toLocaleLowerCase()}` : ''
        }`
      }
      return '/'
    },
    [locationSlug]
  )
  const getPublicParticipationRoute = useCallback(
    (filter?: string) => {
      if (locationSlug) {
        return `/${locationSlug}${PUBLIC_PARTICIPATION_PARTIAL}${
          filter ? `/${filter.toLocaleLowerCase()}` : ''
        }`
      }
      return '/'
    },
    [locationSlug]
  )
  const getInfrastructureProjectsRoute = useCallback(
    (filter?: string) => {
      if (locationSlug) {
        return `/${locationSlug}${INFRASTRUCTURE_PROJECTS_PARTIAL}${
          filter ? `/${filter.toLocaleLowerCase()}` : ''
        }`
      }
      return '/'
    },
    [locationSlug]
  )
  const getProfilesDirectoryRoute = useCallback(() => {
    if (locationSlug) {
      return `/${locationSlug}${FRONTEND_PROFILES_DIRECTORY}`
    }
    return '/'
  }, [locationSlug])

  const getResourceLinkRoute = useCallback(
    (resourceLink: ResourceLink): string => {
      switch (resourceLink.__typename) {
        case 'ActorProfileResourceLink': {
          const { actorProfile } = resourceLink
          switch (actorProfile.__typename) {
            case 'AuthorityProfile':
            case 'BlogProfile':
            case 'PressProfile':
            case 'PrivateProfile':
              return UrlUtils.getProfileFrontendPath(
                actorProfile.slug,
                actorProfile.__typename
              )
            default:
              return getNewsfeedRoute()
          }
        }
        case 'ContentItemResourceLink': {
          const { contentItem } = resourceLink
          switch (contentItem.__typename) {
            case 'Article':
              return `${FRONTEND_ARTICLE_PATH}/${contentItem.slug}`
            case 'Snapshot':
              return `${FRONTEND_SNAPSHOT_PATH}/${contentItem.slug}`
            case 'Event':
              return `${FRONTEND_EVENT_PATH}/${contentItem.slug}`
            default:
              reportError(
                'not supported resouce link path, returning default to newsfeed',
                resourceLink
              )
              return getNewsfeedRoute()
          }
        }
        case 'GroupResourceLink': {
          const { group } = resourceLink
          return UrlUtils.groupMembersPage(group.id)
        }
        case 'ProjectResourceLink': {
          const { project } = resourceLink
          return getProjectRoutes(project.type).milestones(project.slug)
        }
        case 'ProjectMeetingsResourceLink': {
          const { project } = resourceLink
          return getProjectRoutes(project.type).meetings(project.slug)
        }
        // TODO: Implement once available
        // case RESOURCE_LINK_PATH_EVENTS_TOP_PER_CATEGORY:
        //   return getEventsfeedRoute()
        // case RESOURCE_LINK_PATH_EVENTS:
        //   if (filterName) {
        //     if (filterName === textResources.shared.forMe) {
        //       return getEventsfeedRoute()
        //     }
        //     return getEventsfeedRoute(filterName.toLocaleLowerCase())
        //   }
        //   reportError(
        //     'an events path was given to the resourcelink translator, but no filterName added.',
        //     { filterName, resourceLink })
        //   return getEventsfeedRoute()
        // case RESOURCE_LINK_PATH_NEWSFEED:
        //   return getNewsfeedRoute()
        // case RESOURCE_LINK_PATH_CONVERSATION:
        //   if (params.id) {
        //     return `${FRONTEND_CONVERSATION_PATH}${params.id}`
        //   }
        //   return FRONTEND_CONVERSATION_PATH
        // case RESOURCE_LINK_PATH_AUTHORITY_PROFILES:
        // case RESOURCE_LINK_PATH_BLOG_PROFILES:
        // case RESOURCE_LINK_PATH_PRESS_PROFILES:
        // case RESOURCE_LINK_PATH_PRIVATE_PROFILES:
        //   if (params.id) {
        //     return UrlUtils.getProfileFrontendPath(params.id, (path.slice(0, -1) as ProfileType))
        //   }
        //   return getNewsfeedRoute()
        default:
          reportError(
            'not supported resouce link path, returning default to newsfeed',
            resourceLink
          )
          return getNewsfeedRoute()
      }
    },
    [getNewsfeedRoute]
  )

  const navigateToPositionURL = useCallback(
    async (match: match, coordinates: IPosition) => {
      const { data, error } = await lookUpLocation({
        variables: { coordinates }
      })

      const locationShape = data?.geoLookup?.[0]?.locationShape

      if (locationShape) {
        history.push(replaceLocationSlugFromPath(match, locationShape.slug), { keepBoundingBox: true })
      }

      if (error || !locationShape) {
        throw new Error('Could not look up location for given coordinates')
      }
    },
    [lookUpLocation, history]
  )

  const navigateToShape = useCallback(
    (match: match, locationShape: ILocationShape) => {
      history.push(replaceLocationSlugFromPath(match, locationShape.slug))
    },
    [history]
  )

  return (
    <URLContext.Provider
      value={{
        getEventsfeedRoute,
        getPublicParticipationRoute,
        getInfrastructureProjectsRoute,
        getNewsfeedRoute,
        getProfilesDirectoryRoute,
        getResourceLinkRoute,
        navigateToPositionURL,
        navigateToShape
      }}
    >
      {children}
    </URLContext.Provider>
  )
}

export default withRouter(URLProvider)
